/**
 * Athena Brain Admin - KI-Wissen verwalten
 * 
 * Zeigt und verwaltet:
 * - Gelernte Muster
 * - Gedächtnis-Einträge  
 * - Wiki-Synchronisation
 * - Statistiken
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Brain,
  RefreshCw,
  Search,
  Trash2,
  Edit,
  Plus,
  BookOpen,
  TrendingUp,
  Sparkles,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Filter,
  Zap,
  Target,
  Clock,
  ArrowUpRight,
  BarChart3,
  Lightbulb,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Dialog imports removed - all forms are now inline
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Farben für Kategorien
const KATEGORIE_FARBEN = {
  posteingang: 'bg-blue-100 text-blue-700 border-blue-200',
  aufgabe: 'bg-green-100 text-green-700 border-green-200',
  massnahme: 'bg-purple-100 text-purple-700 border-purple-200',
  institution: 'bg-amber-100 text-amber-700 border-amber-200',
  finanzen: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  kommunikation: 'bg-pink-100 text-pink-700 border-pink-200',
  allgemein: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function AthenaBrainPage() {
  const [activeTab, setActiveTab] = useState('muster');
  const [loading, setLoading] = useState(true);
  const [muster, setMuster] = useState([]);
  const [gedaechtnis, setGedaechtnis] = useState([]);
  const [statistiken, setStatistiken] = useState(null);
  const [kategorien, setKategorien] = useState([]);
  const [status, setStatus] = useState(null);
  const [vorschlaege, setVorschlaege] = useState([]);
  
  // Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('alle');
  
  // Dialog State
  const [editDialog, setEditDialog] = useState({ open: false, muster: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, typ: null });

  // Daten laden
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [musterRes, gedaechtnisRes, statsRes, katRes, statusRes, vorschlaegeRes] = await Promise.all([
        fetch(`${API_URL}/api/athena/muster`).then(r => r.json()),
        fetch(`${API_URL}/api/athena/gedaechtnis?limit=100`).then(r => r.json()),
        fetch(`${API_URL}/api/athena/statistiken`).then(r => r.json()),
        fetch(`${API_URL}/api/athena/kategorien`).then(r => r.json()),
        fetch(`${API_URL}/api/athena/status`).then(r => r.json()),
        fetch(`${API_URL}/api/athena/vorschlaege`).then(r => r.json()),
      ]);
      
      setMuster(musterRes.muster || []);
      setGedaechtnis(gedaechtnisRes.eintraege || []);
      setStatistiken(statsRes.statistiken || null);
      setKategorien(katRes.kategorien || []);
      setStatus(statusRes);
      setVorschlaege(vorschlaegeRes.vorschlaege || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Wiki synchronisieren
  const syncWiki = async () => {
    try {
      const res = await fetch(`${API_URL}/api/athena/sync-wiki`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        loadData();
      } else {
        toast.error('Synchronisation fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler bei der Wiki-Synchronisation');
    }
  };

  // Vorschlag akzeptieren
  const handleAcceptVorschlag = async (vorschlagId) => {
    try {
      const res = await fetch(`${API_URL}/api/athena/vorschlaege/${vorschlagId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktion: 'akzeptieren' }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Muster erfolgreich übernommen und im Wiki dokumentiert');
        loadData();
      } else {
        toast.error('Fehler beim Übernehmen');
      }
    } catch (error) {
      toast.error('Fehler beim Übernehmen des Vorschlags');
    }
  };

  // Vorschlag ablehnen
  const handleRejectVorschlag = async (vorschlagId) => {
    try {
      const res = await fetch(`${API_URL}/api/athena/vorschlaege/${vorschlagId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktion: 'ablehnen' }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Vorschlag abgelehnt');
        loadData();
      } else {
        toast.error('Fehler beim Ablehnen');
      }
    } catch (error) {
      toast.error('Fehler beim Ablehnen des Vorschlags');
    }
  };

  // Muster löschen
  const handleDelete = async () => {
    const { id, typ } = deleteDialog;
    try {
      const endpoint = typ === 'muster' 
        ? `${API_URL}/api/athena/muster/${id}`
        : `${API_URL}/api/athena/gedaechtnis/${id}`;
      
      const res = await fetch(endpoint, { method: 'DELETE' });
      const data = await res.json();
      
      if (data.success) {
        toast.success(typ === 'muster' ? 'Muster gelöscht' : 'Eintrag gelöscht');
        loadData();
      } else {
        toast.error('Löschen fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteDialog({ open: false, id: null, typ: null });
    }
  };

  // Gefilterte Muster
  const filteredMuster = muster.filter(m => {
    const matchesSearch = searchTerm === '' || 
      m.beschreibung?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.trigger?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.erkennungsmerkmale?.some(e => e.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesKategorie = filterKategorie === 'alle' || m.kategorie === filterKategorie;
    
    return matchesSearch && matchesKategorie;
  });

  // Muster nach Kategorien gruppieren
  const groupedMuster = filteredMuster.reduce((acc, m) => {
    const kat = m.kategorie || 'allgemein';
    if (!acc[kat]) acc[kat] = [];
    acc[kat].push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="athena-brain-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Brain className="w-7 h-7 text-purple-600" />
            Zeo Brain
          </h1>
          <p className="text-slate-500 mt-1">
            Verwalten Sie das KI-Wissen und die gelernten Muster
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <Button onClick={syncWiki} className="bg-purple-600 hover:bg-purple-700">
            <BookOpen className="w-4 h-4 mr-2" />
            Wiki synchronisieren
          </Button>
        </div>
      </div>

      {/* Status-Karten */}
      {status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatusCard
            icon={<Sparkles className="w-5 h-5 text-purple-600" />}
            label="Status"
            value={status.status === 'active' ? 'Aktiv' : 'Inaktiv'}
            color={status.status === 'active' ? 'text-green-600' : 'text-red-600'}
          />
          <StatusCard
            icon={<Target className="w-5 h-5 text-blue-600" />}
            label="Gelernte Muster"
            value={statistiken?.total_muster || 0}
          />
          <StatusCard
            icon={<Brain className="w-5 h-5 text-amber-600" />}
            label="Gedächtnis-Einträge"
            value={statistiken?.total_gedaechtnis || 0}
          />
          <StatusCard
            icon={<BookOpen className="w-5 h-5 text-green-600" />}
            label="Wiki-Einträge"
            value={statistiken?.wiki_eintraege || 0}
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-auto flex w-full overflow-x-auto no-scrollbar p-1 gap-1">
          {vorschlaege.length > 0 && (
            <TabsTrigger value="vorschlaege" className="flex items-center gap-2 shrink-0 relative">
              <Lightbulb className="w-4 h-4" />
              Vorschläge
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                {vorschlaege.length}
              </span>
            </TabsTrigger>
          )}
          <TabsTrigger value="muster" className="flex items-center gap-2 shrink-0">
            <Target className="w-4 h-4" />
            Muster ({muster.length})
          </TabsTrigger>
          <TabsTrigger value="gedaechtnis" className="flex items-center gap-2 shrink-0">
            <Brain className="w-4 h-4" />
            Gedächtnis ({gedaechtnis.length})
          </TabsTrigger>
          <TabsTrigger value="statistiken" className="flex items-center gap-2 shrink-0">
            <BarChart3 className="w-4 h-4" />
            Statistiken
          </TabsTrigger>
        </TabsList>

        {/* Vorschläge Tab */}
        <TabsContent value="vorschlaege" className="space-y-4">
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-800">Neue Muster erkannt!</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Zeo hat wiederholt ähnliche Dokumente verarbeitet und schlägt neue Erkennungsmuster vor.
                  Überprüfen Sie die Vorschläge und entscheiden Sie, ob sie übernommen werden sollen.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {vorschlaege.map((vorschlag) => (
              <VorschlagCard
                key={vorschlag.vorschlag_id}
                vorschlag={vorschlag}
                onAccept={() => handleAcceptVorschlag(vorschlag.vorschlag_id)}
                onReject={() => handleRejectVorschlag(vorschlag.vorschlag_id)}
              />
            ))}
          </div>
        </TabsContent>

        {/* Muster Tab */}
        <TabsContent value="muster" className="space-y-4">
          {/* Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suche nach Mustern..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-muster"
              />
            </div>
            <Select value={filterKategorie} onValueChange={setFilterKategorie}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Kategorien</SelectItem>
                {kategorien.map(k => (
                  <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setEditDialog({ open: true, muster: null })} data-testid="new-muster-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neues Muster
            </Button>
          </div>

          {/* Muster Liste nach Kategorien */}
          {loading ? (
            <LoadingState />
          ) : Object.keys(groupedMuster).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(groupedMuster).map(([kategorie, musterList]) => (
                <MusterKategorie
                  key={kategorie}
                  kategorie={kategorie}
                  kategorieLabel={kategorien.find(k => k.key === kategorie)?.label || kategorie}
                  musterList={musterList}
                  onEdit={(m) => setEditDialog({ open: true, muster: m })}
                  onDelete={(id) => setDeleteDialog({ open: true, id, typ: 'muster' })}
                />
              ))}
            </div>
          ) : (
            <EmptyState 
              message="Keine Muster gefunden"
              action={() => setEditDialog({ open: true, muster: null })}
              actionLabel="Erstes Muster anlegen"
            />
          )}
        </TabsContent>

        {/* Gedächtnis Tab */}
        <TabsContent value="gedaechtnis" className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">DSGVO-Hinweis</p>
              <p className="text-sm text-amber-700">
                Alle Einträge sind pseudonymisiert. Personenbezogene Daten werden nicht im Klartext gespeichert.
              </p>
            </div>
          </div>

          {loading ? (
            <LoadingState />
          ) : gedaechtnis.length > 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {gedaechtnis.map((eintrag) => (
                <GedaechtnisEintrag
                  key={eintrag.id}
                  eintrag={eintrag}
                  onDelete={(id) => setDeleteDialog({ open: true, id, typ: 'gedaechtnis' })}
                />
              ))}
            </div>
          ) : (
            <EmptyState message="Noch keine Gedächtnis-Einträge" />
          )}
        </TabsContent>

        {/* Statistiken Tab */}
        <TabsContent value="statistiken" className="space-y-6">
          {statistiken ? (
            <>
              {/* Muster nach Kategorie */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  Muster nach Kategorie
                </h3>
                <div className="space-y-3">
                  {Object.entries(statistiken.muster_nach_kategorie || {}).map(([kat, count]) => {
                    const total = statistiken.total_muster || 1;
                    const percent = Math.round((count / total) * 100);
                    return (
                      <div key={kat} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700 capitalize">{kat}</span>
                          <span className="text-slate-500">{count} ({percent}%)</span>
                        </div>
                        <Progress value={percent} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top Muster */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Top 5 erfolgreichste Muster
                </h3>
                <div className="space-y-3">
                  {(statistiken.top_muster || []).map((m, i) => (
                    <div key={m.trigger} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">
                          {i + 1}
                        </span>
                        <span className="text-slate-700">{m.beschreibung || m.trigger}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-600 font-medium">{Math.round(m.erfolgsrate * 100)}%</span>
                        <span className="text-slate-500">{m.nutzungen} Nutzungen</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Aktionen nach Typ */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-600" />
                  Aktionen nach Typ
                </h3>
                {(statistiken.aktionen_nach_typ || []).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {statistiken.aktionen_nach_typ.map((a) => (
                      <div key={a.typ} className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-800">{a.anzahl}</p>
                        <p className="text-sm text-slate-500 capitalize">{a.typ?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-green-600">Score: {a.durchschnitt_score}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Noch keine Aktionen aufgezeichnet</p>
                )}
              </div>

              {/* Lernfortschritt */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Letzte 7 Tage</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {statistiken.neue_eintraege_7_tage || 0} neue Einträge
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <LoadingState />
          )}
        </TabsContent>
      </Tabs>

      {/* Edit/Create Dialog */}
      <MusterEditDialog
        open={editDialog.open}
        muster={editDialog.muster}
        kategorien={kategorien}
        onClose={() => setEditDialog({ open: false, muster: null })}
        onSave={() => {
          setEditDialog({ open: false, muster: null });
          loadData();
        }}
      />

      {/* Inline Lösch-Bestätigung */}
      {deleteDialog.open && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-300 rounded-xl shadow-lg p-4 flex items-center gap-4 max-w-lg" data-testid="inline-delete-confirm">
          <p className="text-sm text-red-800">
            {deleteDialog.typ === 'muster' ? 'Muster-Eintrag' : 'Gedächtnis-Eintrag'} wirklich löschen?
          </p>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setDeleteDialog({ open: false, id: null, typ: null })}>Abbrechen</Button>
            <Button variant="destructive" size="sm" onClick={handleDelete}><Trash2 className="w-3 h-3 mr-1" /> Löschen</Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== KOMPONENTEN ====================

function StatusCard({ icon, label, value, color = 'text-slate-800' }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center">
          {icon}
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className={`text-lg font-bold ${color}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function MusterKategorie({ kategorie, kategorieLabel, musterList, onEdit, onDelete }) {
  const [isOpen, setIsOpen] = useState(true);
  const colorClass = KATEGORIE_FARBEN[kategorie] || KATEGORIE_FARBEN.allgemein;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className={`flex items-center justify-between p-3 rounded-lg border ${colorClass} hover:opacity-90 transition-opacity`}>
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="font-medium">{kategorieLabel}</span>
            <Badge variant="secondary" className="ml-2">{musterList.length}</Badge>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 space-y-2 pl-4">
          {musterList.map((m) => (
            <MusterCard key={m.id} muster={m} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function MusterCard({ muster, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 transition-colors group" data-testid={`muster-${muster.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-slate-800">{muster.beschreibung || muster.trigger}</p>
            <Badge variant="outline" className="text-xs font-mono">{muster.trigger}</Badge>
          </div>
          
          {/* Erkennungsmerkmale */}
          {muster.erkennungsmerkmale?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {muster.erkennungsmerkmale.slice(0, 5).map((m, i) => (
                <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                  {m}
                </span>
              ))}
              {muster.erkennungsmerkmale.length > 5 && (
                <span className="text-xs text-slate-400">+{muster.erkennungsmerkmale.length - 5} mehr</span>
              )}
            </div>
          )}

          {/* Empfohlene Aktionen */}
          {muster.empfohlene_aktionen?.length > 0 && (
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
              <Zap className="w-3 h-3" />
              {muster.empfohlene_aktionen.map((a, i) => (
                <span key={i}>{a.typ}{a.prioritaet ? ` (${a.prioritaet})` : ''}</span>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 mt-3 text-sm">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-green-600" />
              <span className="text-green-600 font-medium">{Math.round(muster.erfolgsrate * 100)}%</span>
            </div>
            <div className="flex items-center gap-1 text-slate-500">
              <Target className="w-3 h-3" />
              <span>{muster.nutzungen || 0} Nutzungen</span>
            </div>
          </div>
        </div>

        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={() => onEdit(muster)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete(muster.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function GedaechtnisEintrag({ eintrag, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  
  const getTypIcon = (typ) => {
    switch (typ) {
      case 'aktion': return <Zap className="w-4 h-4 text-blue-600" />;
      case 'feedback': return <Check className="w-4 h-4 text-green-600" />;
      case 'praeferenz': return <Lightbulb className="w-4 h-4 text-amber-600" />;
      default: return <FileText className="w-4 h-4 text-slate-600" />;
    }
  };

  return (
    <div className="p-4 hover:bg-slate-50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-0.5">{getTypIcon(eintrag.typ)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-800 capitalize">{eintrag.inhalt?.aktion_typ?.replace(/_/g, ' ') || eintrag.typ}</span>
              <Badge variant="outline" className={KATEGORIE_FARBEN[eintrag.kategorie] || ''}>
                {eintrag.kategorie}
              </Badge>
              {eintrag.inhalt?.erfolgreich !== undefined && (
                eintrag.inhalt.erfolgreich 
                  ? <Badge className="bg-green-100 text-green-700">Erfolgreich</Badge>
                  : <Badge className="bg-red-100 text-red-700">Fehlgeschlagen</Badge>
              )}
            </div>
            
            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
              <span>Score: {(eintrag.relevanz_score * 100).toFixed(0)}%</span>
              <span>{new Date(eintrag.created_date).toLocaleString('de-DE')}</span>
            </div>

            {/* Expandable Content */}
            {expanded && eintrag.inhalt && (
              <div className="mt-3 p-3 bg-slate-100 rounded-lg text-xs font-mono text-slate-600 overflow-x-auto">
                <pre className="whitespace-pre-wrap">{JSON.stringify(eintrag.inhalt, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete(eintrag.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function MusterEditDialog({ open, muster, kategorien, onClose, onSave }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    trigger: '',
    beschreibung: '',
    erkennungsmerkmale: '',
    kategorie: 'allgemein',
    unterkategorie: '',
    empfohlene_aktionen: [],
  });

  useEffect(() => {
    if (open) {
      if (muster) {
        setForm({
          trigger: muster.trigger || '',
          beschreibung: muster.beschreibung || '',
          erkennungsmerkmale: (muster.erkennungsmerkmale || []).join(', '),
          kategorie: muster.kategorie || 'allgemein',
          unterkategorie: muster.unterkategorie || '',
          empfohlene_aktionen: muster.empfohlene_aktionen || [],
        });
      } else {
        setForm({
          trigger: '',
          beschreibung: '',
          erkennungsmerkmale: '',
          kategorie: 'allgemein',
          unterkategorie: '',
          empfohlene_aktionen: [],
        });
      }
    }
  }, [open, muster]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.trigger.trim() || !form.beschreibung.trim()) {
      toast.error('Trigger und Beschreibung sind erforderlich');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        trigger: form.trigger.trim(),
        beschreibung: form.beschreibung.trim(),
        erkennungsmerkmale: form.erkennungsmerkmale.split(',').map(s => s.trim()).filter(Boolean),
        kategorie: form.kategorie,
        unterkategorie: form.unterkategorie.trim(),
        empfohlene_aktionen: form.empfohlene_aktionen,
      };

      let res;
      if (muster) {
        res = await fetch(`${API_URL}/api/athena/muster/${muster.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_URL}/api/athena/muster`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json();
      if (data.success) {
        toast.success(muster ? 'Muster aktualisiert' : 'Muster erstellt');
        onSave();
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addAktion = () => {
    setForm(f => ({
      ...f,
      empfohlene_aktionen: [...f.empfohlene_aktionen, { typ: 'aufgabe', prioritaet: 'mittel' }]
    }));
  };

  const updateAktion = (index, field, value) => {
    setForm(f => ({
      ...f,
      empfohlene_aktionen: f.empfohlene_aktionen.map((a, i) => 
        i === index ? { ...a, [field]: value } : a
      )
    }));
  };

  const removeAktion = (index) => {
    setForm(f => ({
      ...f,
      empfohlene_aktionen: f.empfohlene_aktionen.filter((_, i) => i !== index)
    }));
  };

  return (
    open ? (
    <Card className="max-w-lg mx-auto mt-4 border-slate-300 shadow-lg max-h-[80vh] overflow-y-auto" data-testid="muster-form-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-600" />
            {muster ? 'Muster bearbeiten' : 'Neues Muster'}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <CardDescription>Definieren Sie ein Erkennungsmuster für Zeo</CardDescription>
      </CardHeader>
      <CardContent>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="trigger">Trigger (eindeutig) *</Label>
              <Input
                id="trigger"
                value={form.trigger}
                onChange={(e) => setForm(f => ({ ...f, trigger: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                placeholder="z.B. dokument_von_gericht"
                data-testid="muster-trigger"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="beschreibung">Beschreibung *</Label>
              <Input
                id="beschreibung"
                value={form.beschreibung}
                onChange={(e) => setForm(f => ({ ...f, beschreibung: e.target.value }))}
                placeholder="z.B. Dokument kommt von einem Gericht"
                data-testid="muster-beschreibung"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="erkennungsmerkmale">Erkennungsmerkmale (kommagetrennt)</Label>
            <Textarea
              id="erkennungsmerkmale"
              value={form.erkennungsmerkmale}
              onChange={(e) => setForm(f => ({ ...f, erkennungsmerkmale: e.target.value }))}
              placeholder="amtsgericht, landgericht, aktenzeichen, geschäftszeichen"
              rows={2}
            />
            <p className="text-xs text-slate-500 mt-1">Schlüsselwörter zur Erkennung im Text</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Kategorie</Label>
              <Select value={form.kategorie} onValueChange={(v) => setForm(f => ({ ...f, kategorie: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {kategorien.map(k => (
                    <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="unterkategorie">Unterkategorie</Label>
              <Input
                id="unterkategorie"
                value={form.unterkategorie}
                onChange={(e) => setForm(f => ({ ...f, unterkategorie: e.target.value }))}
                placeholder="z.B. Zivilsachen"
              />
            </div>
          </div>

          {/* Empfohlene Aktionen */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Empfohlene Aktionen</Label>
              <Button type="button" variant="outline" size="sm" onClick={addAktion}>
                <Plus className="w-3 h-3 mr-1" /> Aktion
              </Button>
            </div>
            {form.empfohlene_aktionen.length > 0 ? (
              <div className="space-y-2">
                {form.empfohlene_aktionen.map((aktion, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Select value={aktion.typ} onValueChange={(v) => updateAktion(i, 'typ', v)}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aufgabe">Aufgabe</SelectItem>
                        <SelectItem value="massnahme">Maßnahme</SelectItem>
                        <SelectItem value="benachrichtigung">Benachrichtigung</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={aktion.prioritaet || 'mittel'} onValueChange={(v) => updateAktion(i, 'prioritaet', v)}>
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dringend">Dringend</SelectItem>
                        <SelectItem value="mittel">Mittel</SelectItem>
                        <SelectItem value="niedrig">Niedrig</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      className="flex-1"
                      value={aktion.kategorie || ''}
                      onChange={(e) => updateAktion(i, 'kategorie', e.target.value)}
                      placeholder="Kategorie"
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAktion(i)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-2 bg-slate-50 rounded-lg">
                Keine Aktionen definiert
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Wird gespeichert...' : muster ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
    ) : null
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
    </div>
  );
}

function EmptyState({ message, action, actionLabel }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
      <Brain className="w-16 h-16 mx-auto text-slate-300 mb-4" />
      <p className="text-slate-500 mb-4">{message}</p>
      {action && (
        <Button onClick={action}>
          <Plus className="w-4 h-4 mr-2" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
}


function VorschlagCard({ vorschlag, onAccept, onReject }) {
  const [processing, setProcessing] = useState(false);

  const handleAccept = async () => {
    setProcessing(true);
    try {
      await onAccept();
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await onReject();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border-2 border-orange-200 p-5 hover:border-orange-300 transition-colors" data-testid={`vorschlag-${vorschlag.vorschlag_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Lightbulb className="w-5 h-5 text-orange-500" />
            <span className="font-semibold text-slate-800">{vorschlag.beschreibung}</span>
            <Badge variant="outline" className="text-xs font-mono bg-orange-50">{vorschlag.trigger}</Badge>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
            <Target className="w-4 h-4" />
            <span>{vorschlag.anzahl_beobachtungen}x beobachtet</span>
            <span className="text-slate-300">|</span>
            <span className="capitalize">{vorschlag.kategorie}</span>
          </div>

          {/* Erkennungsmerkmale */}
          {vorschlag.erkennungsmerkmale?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-slate-500 mb-1">Erkennungsmerkmale:</p>
              <div className="flex flex-wrap gap-1">
                {vorschlag.erkennungsmerkmale.slice(0, 6).map((m, i) => (
                  <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Empfohlene Aktionen */}
          {vorschlag.empfohlene_aktionen?.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Zap className="w-3 h-3" />
              <span>Empfohlen: {vorschlag.empfohlene_aktionen.map(a => a.typ).join(', ')}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button 
            size="sm" 
            className="bg-green-600 hover:bg-green-700"
            onClick={handleAccept}
            disabled={processing}
          >
            <Check className="w-4 h-4 mr-1" />
            Übernehmen
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleReject}
            disabled={processing}
          >
            <X className="w-4 h-4 mr-1" />
            Ablehnen
          </Button>
        </div>
      </div>
    </div>
  );
}

